import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtCur, fmtDate, fmtNum } from "../lib/format";
import { statusLabel } from "./Shipping";
import { ArrowLeft, Boat, MapPin, Broadcast, CheckCircle, Check, Truck, Train } from "@phosphor-icons/react";

const curCode = (c) => (/(\$|US)/i.test(c || "") ? "USD" : (c || "USD"));

// OTD marker: effective delivery (port ETA + inland transit) vs the PO line due date.
const OTD_STYLES = {
  late: ["#FEE2E2", "#B91C1C"],
  early: ["#DCFCE7", "#166534"],
  on_time: ["#DCFCE7", "#166534"],
  eta_pending: ["#F1F5F9", "#64748B"],
  no_due: ["#F1F5F9", "#94A3B8"],
};
const otdText = (status, delta) => {
  if (status === "late") return `Late by ${delta}d`;
  if (status === "early") return `Early by ${Math.abs(delta)}d`;
  if (status === "on_time") return "On-time";
  if (status === "eta_pending") return "ETA pending";
  if (status === "no_due") return "No Due date";
  return "—";
};
const OtdBadge = ({ status, delta, testid, big }) => {
  if (!status) return <span className="muted">—</span>;
  const [bg, fg] = OTD_STYLES[status] || OTD_STYLES.no_due;
  return (
    <span data-testid={testid} style={{
      display: "inline-block", fontSize: big ? 12 : 10.5, fontWeight: 700,
      padding: big ? "3px 11px" : "2px 8px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap",
    }}>
      {otdText(status, delta)}
    </span>
  );
};

export default function ShipmentDetail() {
  const { containerNo, invNo } = useParams();
  const navigate = useNavigate();
  const byInvoice = !containerNo && !!invNo;
  const detailUrl = byInvoice
    ? `/shipping/shipments/by-invoice/${encodeURIComponent(invNo)}`
    : `/shipping/shipments/${encodeURIComponent(containerNo)}`;
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("contents");
  const [live, setLive] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [bkNo, setBkNo] = useState("");
  const [bkLine, setBkLine] = useState("");
  const [bkSaving, setBkSaving] = useState(false);

  const saveBooking = async () => {
    const b = bkNo.trim(), l = bkLine.trim();
    if (!b || !l) { toast.error("Enter both the booking # and shipping line"); return; }
    setBkSaving(true);
    try {
      const { data } = await api.post("/shipping/booking-ref",
        { booking_no: b, line: l, container_no: s?.container_no || "" });
      toast.success(`Now tracking by booking ${b} (${data.scac}) — ${data.status || "ok"}`);
      const url = data.container_no ? `/shipping/shipments/${encodeURIComponent(data.container_no)}` : detailUrl;
      const r = await api.get(url);
      setS(r.data); const lt = r.data.live_tracking; setLive(lt && lt.pending !== true ? lt : null);
      setBkNo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking track failed");
    } finally { setBkSaving(false); }
  };

  useEffect(() => {
    setS(null); setErr(false); setLive(null);
    api.get(detailUrl)
      .then((r) => {
        // A container turned up for this invoice — send the user to the richer container detail.
        if (r.data && r.data.redirect_container) {
          navigate(`/shipping/${encodeURIComponent(r.data.redirect_container)}`, { replace: true });
          return;
        }
        setS(r.data); const lt = r.data.live_tracking; setLive(lt && lt.pending !== true ? lt : null);
      })
      .catch(() => setErr(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailUrl]);

  const trackLive = async () => {
    if (!containerNo) return;   // live tracking needs a container
    setTracking(true);
    try {
      const { data: kick } = await api.post(`/shipping/shipments/${encodeURIComponent(containerNo)}/track`);
      if (kick && kick.status === "frozen") {
        toast.info(kick.note || "Tracking is frozen — this shipment is delivered/closed.");
        return;
      }
      toast.info("Fetching live tracking… this can take up to a minute.");
      // The fetch runs in the background — poll the detail until the result is ready.
      const startedAt = Date.now();
      let done = false;
      while (Date.now() - startedAt < 90000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get(`/shipping/shipments/${encodeURIComponent(containerNo)}`);
        const lt = data.live_tracking;
        if (lt && lt.pending === false) {
          setS(data);
          if (lt.track_error) {
            toast.error(`Live tracking failed: ${lt.track_error}`);
          } else {
            setLive(lt);
            toast.success(`Live tracking updated — ${statusLabel(lt.status)}${lt.scac ? ` (${lt.scac})` : ""}`);
          }
          done = true;
          break;
        }
      }
      if (!done) toast.info("Still fetching — the update will appear automatically once ready.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Live tracking failed");
    } finally {
      setTracking(false);
    }
  };

  const label = s?.container_no || containerNo || (invNo ? `Invoice #${invNo}` : "");
  if (err) return <Layout breadcrumb={["Home", "Shipping", label]}><div className="jde-page"><Link to="/shipping" className="jde-btn jde-btn-sm"><ArrowLeft size={12} weight="bold" /> Back</Link><div className="muted" style={{ marginTop: 20 }}>Shipment not found.</div></div></Layout>;
  if (!s) return <Layout breadcrumb={["Home", "Shipping", label]}>Loading…</Layout>;

  const code = curCode(s.currency);
  const tk = s.tracking || {};
  const shipEta = live?.eta_date || s.live_tracking?.eta_date || s.eta_date;
  // Actual delivery from the live carrier timeline: the explicit "Delivered by" event is the REAL
  // delivery date (the last actual event is usually the later "Empty returned"). Empty-return is
  // shown separately. DB `delivery_date` is a last-resort fallback (it's usually blank).
  const [liveDeliveredDate, liveEmptyReturnedDate] = (() => {
    const lt = live || (s.live_tracking && s.live_tracking.pending !== true ? s.live_tracking : null);
    if (!lt) return [null, null];
    const acts = (lt.events || []).filter((e) => e && e.type === "actual" && e.event_date);
    const lastWith = (kw) => {
      const m = acts.filter((e) => (e.action || "").toLowerCase().includes(kw));
      return m.length ? m[m.length - 1].event_date : null;
    };
    const delivered = lt.delivered_locked || lt.completed
      || /^(completed|delivered)$/i.test((lt.status || "").trim());
    const deliveredDate = lastWith("deliver")
      || (delivered && acts.length ? acts[acts.length - 1].event_date : null);
    return [deliveredDate, lastWith("empty return")];
  })();

  return (
    <Layout breadcrumb={["Home", "Shipping", s.container_no || `Invoice #${s.inv_no}`]}>
      <div className="jde-page">
        <div className="jde-toolbar">
          <Link to="/shipping" className="jde-btn jde-btn-sm" data-testid="back-to-shipping">
            <ArrowLeft size={12} weight="bold" /> Back
          </Link>
          <div style={{ marginLeft: 12, fontFamily: "var(--jde-font-mono)", fontSize: 15 }}>
            <strong data-testid="ship-detail-container" style={{ fontSize: 17 }}>{s.container_no || `Invoice #${s.inv_no}`}</strong>
            {s.cont_size && <span className="jde-badge" style={{ marginLeft: 8, background: "#E0E7FF", color: "#3730A3", fontWeight: 700, fontSize: 12 }}>{s.cont_size}</span>}
            {!s.container_no && <span className="jde-badge" style={{ marginLeft: 8, background: "#FEE2E2", color: "#991B1B", fontWeight: 700, fontSize: 11 }} data-testid="ship-no-container-badge">NO CONTAINER YET</span>}
            <span className="muted" style={{ marginLeft: 12, fontSize: 14 }}>Customer / TP: <strong>{s.party_name}</strong></span>
            {s.inv_no && s.container_no && <span className="muted" style={{ marginLeft: 12, fontSize: 14 }}>Invoice #{s.inv_no}</span>}
            {s.voc_no && <span className="muted" style={{ marginLeft: 12, fontSize: 14 }}>Voc #{s.voc_no}</span>}
            <span className="jde-badge" style={{ marginLeft: 12, background: "#FEF3C7", color: "#92400E", fontWeight: 700, fontSize: 12 }}>{statusLabel(s.status)}</span>
            {s.otd_status && (
              <span style={{ marginLeft: 12, fontSize: 12 }} title={`Effective delivery = FindTeu port ETA + ${s.otd_inland_days ?? 5} days inland transit, vs the PO line due date`}>
                <span className="muted" style={{ marginRight: 6 }}>Delivery:</span>
                <OtdBadge status={s.otd_status} delta={s.otd_delta_days} big testid="ship-otd-summary" />
              </span>
            )}
          </div>
        </div>

        {/* SUMMARY TILES */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
          <Tile label="Ship Type" value={s.ship_type || "—"} />
          <Tile label="Cartons / Pallets" value={s.pack_qty ? `${fmtNum(s.pack_qty)} ${s.pack_type || ""}`.trim() : "—"} />
          <Tile label="Gross Weight" value={s.gross_wt ? `${fmtNum(s.gross_wt)} kg` : "—"} />
          <Tile label="Invoice Value" value={fmtCur(s.amount, code)} color="#0F766E" />
        </div>

        <div className="jde-tab-strip">
          <button className={`jde-tab ${tab === "contents" ? "active" : ""}`} onClick={() => setTab("contents")} data-testid="tab-contents">What&apos;s Inside ({s.line_count})</button>
          <button className={`jde-tab ${tab === "shipping" ? "active" : ""}`} onClick={() => setTab("shipping")} data-testid="tab-shipping">Shipping &amp; Tracking</button>
        </div>

        <div className="jde-panel">
          {tab === "contents" && (
            <div className="jde-panel-body" style={{ padding: 0, overflowX: "auto" }}>
              {s.lines.length === 0 ? (
                <div className="muted" style={{ padding: 20 }}>No line items found for this shipment's invoice.</div>
              ) : (
                <table className="jde-grid" style={{ minWidth: 900 }} data-testid="ship-contents-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item Code</th>
                      <th>Description</th>
                      <th>HSN</th>
                      <th>PO #</th>
                      <th>Due Date</th>
                      <th>ETA</th>
                      <th>OTD</th>
                      <th style={{ textAlign: "right" }}>Qty</th>
                      <th>Unit</th>
                      {s.is_tp ? (
                        <>
                          <th style={{ textAlign: "right" }} data-testid="col-po-price">PO Price</th>
                          <th style={{ textAlign: "right" }} data-testid="col-po-value">PO Value</th>
                          <th style={{ textAlign: "right" }} data-testid="col-tp-price">TP Price</th>
                          <th style={{ textAlign: "right" }} data-testid="col-tp-value">TP Value</th>
                        </>
                      ) : (
                        <>
                          <th style={{ textAlign: "right" }}>Rate</th>
                          <th style={{ textAlign: "right" }}>Amount</th>
                          <th style={{ textAlign: "right" }} data-testid="col-po-price">PO Price</th>
                          <th style={{ textAlign: "right" }} data-testid="col-po-value">PO Value</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((ln, i) => (
                      <tr key={i} data-testid={`ship-line-${i}`}>
                        <td className="text-mono">{ln.sno || i + 1}</td>
                        <td className="text-mono">{ln.item_code}</td>
                        <td>{ln.description}</td>
                        <td className="text-mono" style={{ fontSize: 13 }}>{ln.hsn || "—"}</td>
                        <td className="text-mono" style={{ fontSize: 13 }}>{ln.po_no || "—"}</td>
                        <td className="text-mono" style={{ fontSize: 13 }} data-testid={`ship-line-${i}-due`}>{ln.due_date ? fmtDate(ln.due_date) : "—"}</td>
                        <td className="text-mono" style={{ fontSize: 13 }} data-testid={`ship-line-${i}-eta`}>{shipEta ? fmtDate(shipEta) : "—"}</td>
                        <td><OtdBadge status={ln.otd_status} delta={ln.otd_delta_days} testid={`ship-line-${i}-otd`} /></td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(ln.qty)}</td>
                        <td className="text-mono">{ln.unit}</td>
                        {s.is_tp ? (
                          <>
                            <td className="text-mono" style={{ textAlign: "right" }}>{ln.po_price != null ? fmtCur(ln.po_price, code) : "—"}</td>
                            <td className="text-mono" style={{ textAlign: "right" }}>{ln.po_value != null ? fmtCur(ln.po_value, code) : "—"}</td>
                            <td className="text-mono" style={{ textAlign: "right" }}>{ln.tp_price != null ? fmtCur(ln.tp_price, code) : "—"}</td>
                            <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}>{ln.tp_value != null ? fmtCur(ln.tp_value, code) : "—"}</td>
                          </>
                        ) : (
                          <>
                            <td className="text-mono" style={{ textAlign: "right" }}>{fmtCur(ln.rate, code)}</td>
                            <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}>{fmtCur(ln.amount, code)}</td>
                            <td className="text-mono" style={{ textAlign: "right" }} data-testid={`ship-line-${i}-po-price`}>{ln.po_price != null ? fmtCur(ln.po_price, code) : "—"}</td>
                            <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }} data-testid={`ship-line-${i}-po-value`}>{ln.po_value != null ? fmtCur(ln.po_value, code) : "—"}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === "shipping" && (
            <div className="jde-panel-body">
              {/* FindTeu live tracking */}
              <div data-testid="ship-tracking-callout" style={{ padding: "14px 16px", borderRadius: 8, border: "1px solid " + (live ? "#0F766E" : "#2563EB"), background: live ? "#F0FDFA" : "#EFF6FF", marginBottom: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <Boat size={22} weight="duotone" color={live ? "#0F766E" : "#2563EB"} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: live ? "#0F766E" : "#1E3A8A" }}>
                        Live Tracking — FindTeu {live && <span className="jde-badge" style={{ marginLeft: 6, background: "#0F766E", color: "white", fontWeight: 700 }}>{live.scac || "LIVE"}</span>}
                      </div>
                      {live ? (
                        <div style={{ fontSize: 13.5, marginTop: 4 }}>
                          <span style={{ fontWeight: 700 }}>{statusLabel(live.status)}</span>
                          {live.completed && <span data-testid="ship-delivered-badge" className="jde-badge confirmed" style={{ marginLeft: 6 }}><CheckCircle size={11} weight="bold" /> Delivered{(live.delivered_date || liveDeliveredDate) ? ` · ${fmtDate(live.delivered_date || liveDeliveredDate)}` : ""}</span>}
                          {live.vessel_name && <span className="muted" style={{ marginLeft: 10 }}>Vessel: <strong>{live.vessel_name}</strong>{live.voyage_nr ? ` · ${live.voyage_nr}` : ""}</span>}
                        </div>
                      ) : (
                        <div className="muted" style={{ fontSize: 12, marginTop: 3, maxWidth: 620, lineHeight: 1.4 }}>{tk.note}</div>
                      )}
                    </div>
                  </div>
                  <button data-testid="ship-track-live" onClick={trackLive} disabled={tracking || !s.findteu_configured || !s.container_no}
                    title={!s.container_no ? "No container assigned yet" : (s.findteu_configured ? "" : "FindTeu API key not configured")}
                    style={{ display: s.container_no ? "flex" : "none", gap: 6, alignItems: "center", padding: "8px 14px", border: "none", borderRadius: 6, background: (tracking || !s.findteu_configured) ? "#94A3B8" : "#2563EB", color: "white", fontSize: 12, fontWeight: 700, cursor: (tracking || !s.findteu_configured) ? "default" : "pointer", flexShrink: 0 }}>
                    <Broadcast size={14} weight="bold" /> {tracking ? "Tracking…" : (live ? "Refresh" : "Track live")}
                  </button>
                </div>
                <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
                  <KV icon={<MapPin size={13} weight="bold" />} k="POL" v={live?.pol?.port || s.pol || "—"} />
                  <KV k="ETD" v={fmtDate(live?.etd_date || s.etd_date)} />
                  <KV icon={<MapPin size={13} weight="bold" />} k="POD" v={live?.pod?.port || s.pod || "—"} />
                  <KV k="ETA" v={fmtDate(live?.eta_date || s.eta_date)} />
                  {!live && <span className="muted" style={{ fontSize: 11 }}>(ETD/ETA from database)</span>}
                  {live && <span className="muted" style={{ fontSize: 11 }}>Updated {fmtDate(live.fetched_at)}</span>}
                </div>
                {live && live.booking_unsupported && (
                  <div data-testid="ship-booking-fallback-note" style={{ marginTop: 12, padding: "10px 12px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, color: "#92400E" }}>
                    <strong>{live.tracking_note || "Booking no longer trackable — showing container tracking."}</strong> The carrier has purged this aged booking from its API, so tracking is by container number instead.
                  </div>
                )}
                {live && (live.reused_suspected || live.delivered_locked) && (
                  <div data-testid="ship-reused-banner" style={{ marginTop: 12, padding: "10px 12px", background: live.reused_suspected ? "#FEF3C7" : "#ECFDF5", border: "1px solid " + (live.reused_suspected ? "#FDE68A" : "#A7F3D0"), borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, color: "#374151" }}>
                    {live.reused_suspected ? (
                      <><strong style={{ color: "#92400E" }}>Container reused for a new booking.</strong> FindTeu tracks the physical box, and this container has since been loaded onto a different voyage. The timeline below is that <em>new</em> voyage — <strong>not</strong> this shipment. Your shipment was delivered earlier; live updates are frozen so its status can't be overwritten.</>
                    ) : (
                      <><strong style={{ color: "#065F46" }}>Delivered — tracking frozen.</strong> This shipment reached its final destination, so live updates are stopped to preserve its completed status (the container may be reused for other bookings).</>
                    )}
                  </div>
                )}
              </div>

              {/* Track by carrier BOOKING NUMBER — voyage-specific, immune to container reuse */}
              {s.findteu_configured && (
                <div data-testid="ship-booking-box" style={{ marginTop: 14, padding: "12px 14px", background: "#F8FAFC", border: "1px solid var(--jde-border)", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>
                    Track by Booking #
                    {live?.ref_mode === "booking" && <span className="jde-badge" style={{ background: "#0F766E", color: "white", marginLeft: 6, fontWeight: 700 }}>ACTIVE</span>}
                  </div>
                  {live?.booking_no && (
                    <div style={{ fontSize: 12, color: "#475569", marginBottom: 8 }}>
                      Currently tracking booking <strong>{live.booking_no}</strong> · {live.line} — voyage-specific, so container reuse can't affect it.
                    </div>
                  )}
                  {(s.booking_no || s.line || s.bl_no) && (
                    <div data-testid="ship-erp-booking-info" style={{ fontSize: 12, color: "#334155", marginBottom: 8, display: "flex", flexWrap: "wrap", gap: 16 }}>
                      {s.booking_no && <span>Booking #: <strong>{s.booking_no}</strong></span>}
                      {s.line && <span>Line: <strong>{s.line}</strong></span>}
                      {s.bl_no && <span>B/L: <strong>{s.bl_no}</strong></span>}
                    </div>
                  )}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <input data-testid="ship-booking-no" value={bkNo} onChange={(e) => setBkNo(e.target.value)} placeholder="Booking # (e.g. CAD0894240)" className="jde-input jde-input-sm" style={{ width: 210, fontSize: 12 }} />
                    <input data-testid="ship-booking-line" value={bkLine} onChange={(e) => setBkLine(e.target.value)} placeholder="Line (CMA, HAPAG, MAERSK…)" list="ship-line-options" className="jde-input jde-input-sm" style={{ width: 195, fontSize: 12 }} />
                    <datalist id="ship-line-options">
                      <option value="CMA" /><option value="HAPAG" /><option value="MAERSK" /><option value="MSC" /><option value="ONE" /><option value="HMM" /><option value="COSCO" /><option value="EVERGREEN" /><option value="OOCL" /><option value="YANG MING" /><option value="ZIM" /><option value="APL" />
                    </datalist>
                    <button data-testid="ship-booking-save" onClick={saveBooking} disabled={bkSaving} className="jde-btn jde-btn-sm jde-btn-primary" style={{ fontSize: 12 }}>
                      {bkSaving ? "Tracking…" : "Track by booking"}
                    </button>
                  </div>
                </div>
              )}


              {/* Live event timeline — horizontal train-tracking style */}
              {live && live.events && live.events.length > 0 && (
                <MilestoneTimeline events={live.events} />
              )}

              <table className="jde-detail-grid">
                <tbody>
                  <Row k="Container No" v={s.container_no} />
                  <Row k="Container Size" v={s.cont_size || "—"} />
                  <Row k="Container Type" v={s.cont_type || "—"} />
                  <Row k="E-Seal #" v={s.eseal || "—"} />
                  <Row k="Line Seal #" v={s.line_seal || "—"} />
                  <Row k="To ICD" v={s.to_icd || "—"} />
                  <Row k="Port of Loading (POL)" v={s.pol || "—"} />
                  <Row k="Port of Discharge (POD)" v={s.pod || "—"} />
                  <Row k="ETD (from DB)" v={fmtDate(s.etd_date)} />
                  <Row k="ETA (from DB)" v={fmtDate(s.eta_date)} />
                  <Row k="Delivery Date" v={fmtDate(live?.delivered_date || liveDeliveredDate || s.delivery_date)} />
                  {liveEmptyReturnedDate && <Row k="Empty Returned" v={fmtDate(liveEmptyReturnedDate)} />}
                  <Row k="Container Status" v={statusLabel(s.status)} />
                  <Row k="Ship Type" v={s.ship_type || "—"} />
                  <Row k="Cartons / Pallets" v={s.pack_qty ? `${fmtNum(s.pack_qty)} ${s.pack_type || ""}`.trim() : "—"} />
                  <Row k="Gross Weight" v={s.gross_wt ? `${fmtNum(s.gross_wt)} kg` : "—"} />
                  <Row k="Net Weight" v={s.net_wt ? `${fmtNum(s.net_wt)} kg` : "—"} />
                  <Row k="Dispatch Voc #" v={s.voc_no || "—"} />
                  <Row k="Invoice #" v={s.inv_no || "—"} />
                  <Row k="SAP Sale #" v={s.sale_no || "—"} />
                  <Row k="Customer / Transfer Party" v={s.party_name} />
                  <Row k="PO Reference" v={s.po_nos || "—"} />
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function modeIcon(mode, size = 14) {
  const m = (mode || "").toLowerCase();
  if (m.includes("rail") || m.includes("train")) return <Train size={size} weight="fill" />;
  if (m.includes("truck") || m.includes("road")) return <Truck size={size} weight="fill" />;
  if (m.includes("vessel") || m.includes("sea") || m.includes("ship") || m.includes("ocean") || m.includes("barge")) return <Boat size={size} weight="fill" />;
  return null;
}

function MilestoneTimeline({ events }) {
  const TEAL = "#0F766E", GREY = "#CBD5E1";
  const lastActual = events.reduce((acc, e, i) => (e.type === "actual" ? i : acc), -1);
  const seg = (on, hidden) => ({
    flex: 1, height: 4, borderRadius: 2,
    background: hidden ? "transparent" : (on ? TEAL : "transparent"),
    backgroundImage: (!hidden && !on) ? `repeating-linear-gradient(90deg, ${GREY} 0 6px, transparent 6px 12px)` : "none",
  });
  return (
    <div style={{ marginBottom: 22, background: "white", border: "1px solid var(--jde-border)", borderRadius: 12, padding: "18px 6px 6px", boxShadow: "0 1px 2px rgba(15,23,42,.04)" }} data-testid="ship-events">
      <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, color: "#334155", padding: "0 14px 14px", display: "flex", alignItems: "center", gap: 8 }}>
        <Boat size={18} weight="duotone" /> Shipment Journey
      </div>
      <div style={{ display: "flex", overflowX: "auto", paddingBottom: 12 }} data-testid="ship-timeline">
        {events.map((e, i) => {
          const reached = i <= lastActual;
          const isCurrent = i === lastActual;
          return (
            <div key={i} style={{ flex: "0 0 auto", width: 184, padding: "0 2px" }} data-testid={`ship-milestone-${i}`}>
              {/* rail + node */}
              <div style={{ display: "flex", alignItems: "center", height: 40 }}>
                <div style={seg(i <= lastActual, i === 0)} />
                <div style={{ position: "relative", width: 34, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                  {isCurrent && <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 34, height: 34, borderRadius: "50%", background: "rgba(15,118,110,.14)" }} />}
                  <span style={{ position: "relative", width: reached ? 24 : 18, height: reached ? 24 : 18, borderRadius: "50%", background: reached ? TEAL : "white", border: reached ? "2px solid white" : `2px solid ${GREY}`, boxShadow: reached ? `0 0 0 2px ${TEAL}` : "none", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
                    {reached && <Check size={13} weight="bold" />}
                  </span>
                </div>
                <div style={seg(i < lastActual, i === events.length - 1)} />
              </div>
              {/* label */}
              <div style={{ textAlign: "center", padding: "12px 8px 6px" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: reached ? "#0f172a" : "#64748b", lineHeight: 1.25 }}>{e.action || "—"}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: reached ? TEAL : "#94a3b8", marginTop: 4 }}>{fmtDate(e.event_date)}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3, lineHeight: 1.35 }}>{[e.port, e.country].filter(Boolean).join(", ") || "—"}</div>
                {(e.transport_mode || e.vessel_name) && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8, padding: "3px 9px", borderRadius: 999, background: reached ? "#ecfdf5" : "#f1f5f9", color: reached ? "#0f766e" : "#64748b", fontSize: 11, fontWeight: 600, maxWidth: "100%" }}>
                    {modeIcon(e.transport_mode)}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.transport_mode || ""}{e.vessel_name ? ` · ${e.vessel_name}${e.voyage_nr ? ` (${e.voyage_nr})` : ""}` : ""}
                    </span>
                  </div>
                )}
                {!reached && <div style={{ marginTop: 7, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, color: "#94a3b8" }}>EXPECTED</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Tile({ label, value, color }) {
  return (
    <div style={{ background: "white", border: "1px solid var(--jde-border)", padding: "14px 16px", borderRadius: 6 }}>
      <div className="muted" style={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--jde-font-mono)", color: color || "#0F172A", marginTop: 5 }}>{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <tr>
      <td className="muted" style={{ width: 260, fontSize: 14, fontWeight: 600, padding: "7px 0", verticalAlign: "top" }}>{k}</td>
      <td className="text-mono" style={{ fontSize: 15, padding: "7px 0", fontWeight: 600, color: "#0f172a" }}>{v ?? "—"}</td>
    </tr>
  );
}

function KV({ icon, k, v }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13.5 }}>
      {icon}<span className="muted" style={{ fontWeight: 600 }}>{k}:</span> <span className="text-mono" style={{ fontWeight: 700 }}>{v}</span>
    </div>
  );
}
